/**
 * Scene Store (Zustand)
 * Verwaltet Scenes – mit Persistierung via FastAPI Backend.
 *
 * Migration Phase 2: Verwendet jetzt /api/v1/scenes CRUD-Endpunkte.
 * localStorage-Fallback: Bei statischem Deploy (kein Backend) werden Scenes
 * automatisch im Browser gespeichert und von dort wiederhergestellt.
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { subscribeWithSelector } from 'zustand/middleware'
import {
  Scene,
  SceneViewerState,
  ScenesData,
} from '../types/annotations'
import { API_V1, apiFetch, projectUrl } from '../lib/apiBase'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function now(): string {
  return new Date().toISOString()
}

// ─── localStorage Fallback (statischer Deploy ohne Backend) ─────────────────

const LS_KEY_SCENES = 'orto_store_scenes'

function _lsSave(key: string, data: unknown) {
  try { localStorage.setItem(key, JSON.stringify(data)) } catch { /* quota */ }
}

function _lsLoad<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : null
  } catch { return null }
}

let _lsPersistTimer: ReturnType<typeof setTimeout> | null = null
function _lsPersistScenes(getState: () => { scenes: Scene[] }) {
  if (_lsPersistTimer) clearTimeout(_lsPersistTimer)
  _lsPersistTimer = setTimeout(() => {
    _lsSave(LS_KEY_SCENES, getState().scenes)
  }, 500)
}

// ─── API Helpers ─────────────────────────────────────────────────────────────

async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(projectUrl(path))
  if (!res.ok) throw new Error(`GET ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(projectUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`POST ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(projectUrl(path), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`PATCH ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(projectUrl(path), { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw new Error(`DELETE ${path}: HTTP ${res.status}`)
}

// ─── Store Interface ──────────────────────────────────────────────────────────

interface SceneStore {
  // ── Data ──────────────────────────────────────────────────────────────────
  scenes: Scene[]
  isLoading: boolean
  isSaving: boolean
  lastSaveError: string | null

  // ── UI State ──────────────────────────────────────────────────────────────
  activeSceneId: string | null

  // ── Server I/O ────────────────────────────────────────────────────────────
  loadFromServer: () => Promise<void>
  /** @deprecated — Legacy Bulk-Save, nur noch als Fallback */
  saveToServer: () => Promise<void>

  // ── CRUD ──────────────────────────────────────────────────────────────────
  createScene: (
    partial: Pick<Scene, 'title' | 'viewerState'> &
      Partial<Pick<Scene, 'projectId' | 'cloudId' | 'description' | 'activeCloudIds' | 'annotationIds' | 'auditIds' | 'viewMode' | 'viewTargetName' | 'thumbnailSmall' | 'thumbnailMedium' | 'thumbnailHigh'>>
  ) => Scene
  updateScene: (id: string, patch: Partial<Omit<Scene, 'id' | 'createdAt'>>) => void
  deleteScene: (id: string) => void
  getScene: (id: string) => Scene | undefined
  getScenesByCloud: (cloudId: string) => Scene[]
  getScenesByProject: (projectId: string) => Scene[]

  // ── Membership ────────────────────────────────────────────────────────────
  toggleAnnotationInScene: (sceneId: string, annotationId: string) => void
  toggleAuditInScene: (sceneId: string, auditId: string) => void

  // ── Activation ────────────────────────────────────────────────────────────
  // opts.animate: Kamerafahrt vom aktuellen View zur Scene-Kamera (statt Snap)
  activateScene: (sceneId: string, opts?: { animate?: boolean; duration?: number }) => void
  deactivateScene: () => void

  // ── Start Scene ──────────────────────────────────────────────────────────
  setStartScene: (sceneId: string | null) => void
  getStartScene: (projectId?: string) => Scene | undefined
  activateStartScene: (projectId?: string) => void
}

// ─── Store Implementation ─────────────────────────────────────────────────────

export const useSceneStore = create<SceneStore>()(
  subscribeWithSelector(immer((set, get) => ({
    scenes: [],
    isLoading: false,
    isSaving: false,
    lastSaveError: null,
    activeSceneId: null,

    // ── Server I/O ─────────────────────────────────────────────────────────

    loadFromServer: async () => {
      set(s => { s.isLoading = true })

      try {
        // Neuer CRUD-Endpunkt
        const scenes = await apiGet<Scene[]>('/scenes')
        set(s => { s.scenes = scenes ?? [] })
      } catch {
        console.warn('[orto] Scenes v1 API nicht erreichbar, versuche Legacy...')
        // Fallback: Legacy Bulk-Endpunkt
        try {
          const res = await apiFetch('/api/scenes')
          if (res.ok) {
            const raw = await res.json()
            const data: ScenesData = raw.data || raw
            set(s => { s.scenes = data.scenes ?? [] })
          } else { throw new Error('legacy 404') }
        } catch {
          // Fallback 2: statische Datei
          try {
            const res = await fetch('/scenes.json')
            if (res.ok) {
              const data: ScenesData = await res.json()
              set(s => { s.scenes = data.scenes ?? [] })
            } else { throw new Error('static 404') }
          } catch {
            // Fallback 3: localStorage (statischer Deploy)
            const lsScenes = _lsLoad<Scene[]>(LS_KEY_SCENES)
            if (lsScenes) {
              console.log('[orto] Scenes aus localStorage geladen')
              set(s => { s.scenes = lsScenes })
            }
          }
        }
      } finally {
        set(s => { s.isLoading = false })
      }
    },

    /** Legacy Bulk-Save als Fallback */
    saveToServer: async () => {
      const state = get()
      set(s => { s.isSaving = true; s.lastSaveError = null })
      const payload: ScenesData = { version: 1, scenes: state.scenes }
      try {
        const res = await apiFetch('/api/scenes', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
        console.error('[orto] Scenes speichern fehlgeschlagen:', msg)
        set(s => { s.lastSaveError = msg })
      } finally {
        set(s => { s.isSaving = false })
      }
    },

    // ── CRUD ───────────────────────────────────────────────────────────────

    createScene: (partial) => {
      const id = generateId()
      const ts = now()
      const scene: Scene = {
        id,
        title: partial.title,
        description: partial.description ?? '',
        projectId: partial.projectId,
        cloudId: partial.cloudId,
        viewerState: partial.viewerState,
        activeCloudIds: partial.activeCloudIds ?? [],
        annotationIds: partial.annotationIds ?? [],
        auditIds: partial.auditIds ?? [],
        createdAt: ts,
        updatedAt: ts,
        viewMode: partial.viewMode ?? 'modell',
        viewTargetName: partial.viewTargetName,
        thumbnailSmall: partial.thumbnailSmall,
        thumbnailMedium: partial.thumbnailMedium,
        thumbnailHigh: partial.thumbnailHigh,
      }

      // Payload VOR set() bauen — nach set() sind Objekte Immer-Proxies
      const payload = JSON.parse(JSON.stringify({
        id,
        title: scene.title,
        description: scene.description,
        projectId: scene.projectId,
        cloudId: scene.cloudId,
        viewerState: scene.viewerState,
        activeCloudIds: scene.activeCloudIds,
        annotationIds: scene.annotationIds,
        auditIds: scene.auditIds,
        viewMode: scene.viewMode,
        viewTargetName: scene.viewTargetName,
        isStartScene: false,
        thumbnailSmall: scene.thumbnailSmall || undefined,
        thumbnailMedium: scene.thumbnailMedium || undefined,
        thumbnailHigh: scene.thumbnailHigh || undefined,
      }))

      set(s => { s.scenes.push(scene) })

      // WICHTIG: setTimeout(0) um den fetch AUSSERHALB des Zustand/Immer
      // Execution-Kontexts auszuführen. Direkte fetch()-Aufrufe innerhalb
      // von Zustand+Immer Store-Actions werden stillschweigend nicht ausgeführt.
      const url = projectUrl('/scenes')
      const body = JSON.stringify(payload)
      setTimeout(() => {
        console.log('[orto] Scene speichern →', url, id)
        apiFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        }).then(res => {
          if (!res.ok) throw new Error(`POST /scenes: HTTP ${res.status}`)
          console.log('[orto] Scene gespeichert ✓', id)
          return res.json()
        }).catch(err => {
          console.error('[orto] Scene erstellen fehlgeschlagen:', err)
        })
      }, 0)

      return scene
    },

    updateScene: (id, patch) => {
      // Patch-Daten VOR set() deep-clonen (Immer-Proxy-sicher)
      const safePatch = JSON.parse(JSON.stringify(patch))

      set(s => {
        const idx = s.scenes.findIndex(sc => sc.id === id)
        if (idx === -1) return
        Object.assign(s.scenes[idx], patch, { updatedAt: now() })
      })

      const url = projectUrl(`/scenes/${id}`)
      const body = JSON.stringify(safePatch)
      setTimeout(() => {
        apiFetch(url, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body,
        }).then(res => {
          if (!res.ok) throw new Error(`PATCH /scenes/${id}: HTTP ${res.status}`)
        }).catch(err => {
          console.error('[orto] Scene update fehlgeschlagen:', err)
        })
      }, 0)
    },

    deleteScene: (id) => {
      set(s => {
        s.scenes = s.scenes.filter(sc => sc.id !== id)
        if (s.activeSceneId === id) s.activeSceneId = null
      })

      const url = projectUrl(`/scenes/${id}`)
      setTimeout(() => {
        apiFetch(url, { method: 'DELETE' }).catch(err => {
          console.error('[orto] Scene löschen fehlgeschlagen:', err)
        })
      }, 0)
    },

    getScene: (id) => get().scenes.find(sc => sc.id === id),

    getScenesByCloud: (cloudId) =>
      get().scenes.filter(sc => sc.cloudId === cloudId),

    getScenesByProject: (projectId) =>
      get().scenes.filter(sc => sc.projectId === projectId),

    // ── Membership ─────────────────────────────────────────────────────────

    toggleAnnotationInScene: (sceneId, annotationId) => {
      set(s => {
        const scene = s.scenes.find(sc => sc.id === sceneId)
        if (!scene) return
        const idx = scene.annotationIds.indexOf(annotationId)
        if (idx === -1) {
          scene.annotationIds.push(annotationId)
        } else {
          scene.annotationIds.splice(idx, 1)
        }
        scene.updatedAt = now()
      })

      const scene = get().getScene(sceneId)
      if (scene) {
        const ids = JSON.parse(JSON.stringify(scene.annotationIds))
        const url = projectUrl(`/scenes/${sceneId}`)
        const body = JSON.stringify({ annotationIds: ids })
        setTimeout(() => {
          apiFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body,
          }).catch(err => console.error('[orto] Scene membership update fehlgeschlagen:', err))
        }, 0)
      }
    },

    toggleAuditInScene: (sceneId, auditId) => {
      set(s => {
        const scene = s.scenes.find(sc => sc.id === sceneId)
        if (!scene) return
        const idx = scene.auditIds.indexOf(auditId)
        if (idx === -1) {
          scene.auditIds.push(auditId)
        } else {
          scene.auditIds.splice(idx, 1)
        }
        scene.updatedAt = now()
      })

      const scene = get().getScene(sceneId)
      if (scene) {
        const ids = JSON.parse(JSON.stringify(scene.auditIds))
        const url = projectUrl(`/scenes/${sceneId}`)
        const body = JSON.stringify({ auditIds: ids })
        setTimeout(() => {
          apiFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body,
          }).catch(err => console.error('[orto] Scene membership update fehlgeschlagen:', err))
        }, 0)
      }
    },

    // ── Activation ─────────────────────────────────────────────────────────

    activateScene: (sceneId, opts) => {
      const scene = get().getScene(sceneId)
      if (!scene) return
      set(s => { s.activeSceneId = sceneId })

      const viewMode = scene.viewMode || 'modell'
      const restoreOpts = opts && (opts.animate || typeof opts.duration === 'number')
        ? { animate: !!opts.animate, duration: opts.duration }
        : undefined

      // WICHTIG: Reihenfolge ist entscheidend!
      // 1) Zuerst restoreFullViewerState — macht einen vollständigen Reset
      //    (entfernt ALLE Clip-Volumes, setzt Section/Floorplan-State zurück,
      //     viewTab auf 'modell') und stellt dann Kamera, ClipBox, Renderer etc. her.
      // 2) Danach activateSceneViewMode — wechselt in den richtigen Ansichtsmodus
      //    (Modell/Schnitt/Grundriss). Muss NACH dem Restore kommen, da der Reset
      //    sonst die vom ViewMode erzeugten Clip-Volumes wieder entfernen würde.

      const vs = scene.viewerState
      if (vs && (window as any).restoreFullViewerState) {
        ;(window as any).restoreFullViewerState(vs, restoreOpts)
        // Legacy-Fallback: Wenn die Scene keinen `clouds`-State hat (alte Scenes mit nur
        // {camera, clipBox}), würde restoreFullViewerState alle Wolken unsichtbar lassen.
        // In diesem Fall die Sichtbarkeit aus scene.activeCloudIds bzw. scene.cloudId herstellen.
        if (!vs.clouds?.length && (window as any).restoreActiveCloudIds) {
          const fallbackIds: string[] =
            scene.activeCloudIds?.length ? scene.activeCloudIds
            : scene.cloudId ? [scene.cloudId]
            : []
          if (fallbackIds.length) {
            ;(window as any).restoreActiveCloudIds(fallbackIds)
          }
        }
      } else if (vs && (window as any).restoreSceneViewerState) {
        // Fallback: nur Kamera + ClipBox (alte Szenen)
        ;(window as any).restoreSceneViewerState(vs)
        if (scene.activeCloudIds?.length && (window as any).restoreActiveCloudIds) {
          ;(window as any).restoreActiveCloudIds(scene.activeCloudIds)
        } else if (scene.cloudId && (window as any).restoreActiveCloudIds) {
          ;(window as any).restoreActiveCloudIds([scene.cloudId])
        }
      }

      // View-Mode wechseln (Modell/Grundriss/Schnitt) — NACH dem Restore,
      // damit die vom ViewMode erzeugten Clip-Volumes nicht vom Reset entfernt werden
      if ((window as any).activateSceneViewMode) {
        ;(window as any).activateSceneViewMode(viewMode, scene.viewTargetName || null)
      }
    },

    deactivateScene: () => {
      // Nur den Scene-Highlight aufheben — Viewer-State (Kamera, ClipBox, Wolken)
      // bleibt unverändert, damit der User seinen aktuellen Blick behält.
      set(s => { s.activeSceneId = null })
    },

    // ── Start Scene ─────────────────────────────────────────────────────────

    setStartScene: (sceneId) => {
      set(s => {
        for (const sc of s.scenes) {
          sc.isStartScene = false
        }
        if (sceneId) {
          const scene = s.scenes.find(sc => sc.id === sceneId)
          if (scene) {
            scene.isStartScene = true
            scene.updatedAt = now()
          }
        }
      })

      // Alle Scenes updaten die sich geändert haben
      const scenes = get().scenes
      const updates = scenes.map(sc => ({
        url: projectUrl(`/scenes/${sc.id}`),
        body: JSON.stringify({ isStartScene: sc.isStartScene ?? false }),
      }))
      setTimeout(() => {
        for (const u of updates) {
          apiFetch(u.url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: u.body,
          }).catch(err => console.error('[orto] setStartScene fehlgeschlagen:', err))
        }
      }, 0)
    },

    getStartScene: (projectId) => {
      return get().scenes.find(sc =>
        sc.isStartScene && (!projectId || sc.projectId === projectId)
      )
    },

    activateStartScene: (projectId) => {
      const startScene = get().getStartScene(projectId)
      if (startScene) {
        get().activateScene(startScene.id)
      }
    },
  })))
)

// ─── Expose for script.js Animation Engine ────────────────────────────────────
;(window as any).__zustandSceneStore = useSceneStore

// ─── Auto-Persist nach localStorage (für statischen Deploy) ─────────────────
useSceneStore.subscribe(
  (s) => s.scenes,
  () => _lsPersistScenes(useSceneStore.getState),
)
